import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { EnrichedStopPlace } from '../../../../types';
import { JoreStopRegistryTransportModeType } from '../../../../types/stop-registry';
import { mapLngLatToPoint } from '../../../../utils';
import { mapToISODate } from '../../../../utils/time';
import { FormActionButtons, ValidityPeriodForm } from '../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../common/Inputs';
import { FormColumn, FormRow } from '../../../common/LayoutComponents';
import {
  StopAreaFormState,
  stopAreaFormSchema,
} from '../../../StopRegistry/StopAreas/Common';
import { StopAreaNames } from './StopAreaNames';
import { TransportationModeField } from './TransportationModeFormField';

const testIds = {
  form: 'StopAreaFormComponent::form',
  showHideButton: 'StopAreaFormComponent::showHideButton',
  privateCode: 'StopAreaFormComponent::privateCode',
  name: 'StopAreaFormComponent::name',
  latitude: 'StopAreaFormComponent::latitude',
  longitude: 'StopAreaFormComponent::longitude',
  quays: 'StopAreaFormComponent::quays',
};

export function mapStopAreaDataToFormState(
  stopArea: EnrichedStopPlace,
): StopAreaFormState {
  const { latitude, longitude } = mapLngLatToPoint(
    stopArea?.geometry?.coordinates ?? [],
  );

  return {
    id: stopArea.id ?? undefined,
    privateCode: stopArea.privateCode?.value ?? '',
    name: stopArea.name ?? '',
    nameSwe: stopArea.nameSwe,
    nameEng: stopArea.nameEng,
    nameLongFin: stopArea.nameLongFin,
    nameLongSwe: stopArea.nameLongSwe,
    nameLongEng: stopArea.nameLongEng,
    abbreviationFin: stopArea.abbreviationFin,
    abbreviationSwe: stopArea.abbreviationSwe,
    abbreviationEng: stopArea.abbreviationEng,
    latitude,
    longitude,
    transportMode: stopArea.transportMode ?? undefined,
    validityStart: mapToISODate(stopArea.validityStart) ?? '',
    validityEnd: mapToISODate(stopArea.validityEnd),
    indefinite: !stopArea.validityEnd,
  };
}

type StopAreaFormProps = {
  readonly className?: string;
  readonly defaultValues:
    StopAreaFormState | (() => Promise<StopAreaFormState>);
  readonly onCancel: () => void;
  readonly onSubmit: (changes: StopAreaFormState) => void;
  readonly testIdPrefix: string;
  readonly availableTransportModes: ReadonlyArray<JoreStopRegistryTransportModeType> | null;
  readonly loadingTransportModes?: boolean;
  readonly enableTransportModeAutoSelect?: boolean;
};

const StopAreaFormComponent: ForwardRefRenderFunction<
  HTMLFormElement,
  StopAreaFormProps
> = (
  {
    className,
    defaultValues,
    onCancel,
    onSubmit,
    testIdPrefix,
    availableTransportModes,
    loadingTransportModes,
    enableTransportModeAutoSelect,
  },
  ref,
) => {
  const methods = useForm<StopAreaFormState>({
    defaultValues,
    resolver: zodResolver(stopAreaFormSchema),
    mode: 'onChange',
  });
  useDirtyFormBlockNavigation(methods.formState, 'StopAreaForm', {
    allowSearchChange: true, // Allow search change so that moving the map does not show the navigation blocked dialog
    allowStateChange: true, // Allow state change so that location state updates do not show the navigation blocked dialog
  });
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        data-testid={testIds.form}
        className={twMerge('flex flex-col', className)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="space-y-6 overflow-auto">
          <FormColumn className="bg-background p-4">
            <div className="flex gap-4">
              <InputField<StopAreaFormState>
                type="text"
                translationPrefix="stopArea"
                fieldPath="privateCode"
                testId={testIds.privateCode}
                className="w-2/5"
                disabled
                required
              />
              <InputField<StopAreaFormState>
                type="text"
                translationPrefix="stopArea"
                fieldPath="name"
                testId={testIds.name}
                className="w-full"
                required
              />
            </div>

            <FormRow>
              <StopAreaNames />
            </FormRow>
          </FormColumn>
          <FormColumn>
            <FormRow className="border-t border-light-grey px-4 py-4">
              <TransportationModeField
                availableTransportModes={availableTransportModes}
                loadingTransportModes={loadingTransportModes}
                enableAutoSelect={enableTransportModeAutoSelect}
              />
            </FormRow>
            <FormRow
              mdColumns={2}
              className="px-4 sm:gap-x-4 md:gap-x-4 lg:gap-x-4"
            >
              <InputField<StopAreaFormState>
                type="number"
                translationPrefix="map"
                fieldPath="latitude"
                testId={testIds.latitude}
                step="any"
                required
              />
              <InputField<StopAreaFormState>
                type="number"
                translationPrefix="map"
                fieldPath="longitude"
                testId={testIds.longitude}
                step="any"
                required
              />
            </FormRow>
            <FormRow className="border-t border-light-grey p-4">
              <ValidityPeriodForm dateInputRowClassName="sm:gap-x-4 md:gap-x-4 lg:gap-x-4" />
            </FormRow>
          </FormColumn>
        </div>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty ||
            methods.formState.isSubmitting ||
            !methods.formState.isValid
          }
          isSubmitting={methods.formState.isSubmitting}
          variant="modal"
        />
      </form>
    </FormProvider>
  );
};

export const StopAreaForm = forwardRef(StopAreaFormComponent);
